import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { getInstructorDashboardSummary } from "action/report";
import { instructorDashboardSummary } from "constants/report";

const renderSummaryCard = (card, navigate) => (
  <Col xl="3" md="6" sm="6" xs="12" key={card.title} className="mb-4">
    <Card
      className={`dashboard-summary-card dashboard-summary-card--${card.theme}`}
      onClick={() => navigate(card.path)}
      role="link"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate(card.path);
        }
      }}
    >
      <CardBody>
        <div className="dashboard-summary-card__content">
          <p className="dashboard-summary-card__title">{card.title}</p>
          <h2 className="dashboard-summary-card__value">{card.value}</h2>
          <p className="dashboard-summary-card__subtitle">{card.subtitle}</p>
        </div>

        <div className="dashboard-summary-card__watermark" aria-hidden="true">
          <i className={card.icon} />
        </div>
      </CardBody>
    </Card>
  </Col>
);

const renderSectionHeader = (
  title,
  subtitle,
  path,
  navigate,
  buttonText = "View All",
) => (
  <CardHeader className="d-flex align-items-center justify-content-between">
    <div>
      <CardTitle tag="h3" className="mb-0 text-default">
        {title}
      </CardTitle>
      {subtitle && <p>{subtitle}</p>}
    </div>

    {path && (
      <Button
        color="link"
        className="dashboard-view-all"
        onClick={() => navigate(path)}
      >
        {buttonText}
        <i className="ni ni-bold-right" />
      </Button>
    )}
  </CardHeader>
);

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const dashboardResponse = useSelector(
    (state) => state.report.instructorDashboardSummary,
  );

  const dashboardData = dashboardResponse?.data?.summary
    ? dashboardResponse.data
    : dashboardResponse;

  const [pageNum, setPageNum] = useState(1);

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    getInstructorDashboardSummary(dispatch, pageNum);

    return () => {
      dispatch({
        type: instructorDashboardSummary,
        payload: {},
      });
    };
  }, [dispatch, pageNum]);

  useEffect(() => {
    if (dashboardData?.summary || dashboardData?.upcomingBatches) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: Array.isArray(dashboardData?.upcomingBatches)
          ? dashboardData.upcomingBatches.length === 0
          : false,
        showLoaderOnClick: false,
      });
    } else if (
      dashboardData?.message ||
      dashboardData?.code >= 400 ||
      dashboardResponse?.message ||
      dashboardResponse?.code >= 400
    ) {
      setListState({
        isLoading: false,
        error:
          dashboardData?.message ||
          dashboardResponse?.message ||
          "Failed to load trainer dashboard. Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [dashboardData, dashboardResponse]);

  const summary = dashboardData?.summary || {};
  const upcomingBatchesData = dashboardData?.upcomingBatches || [];

  const tableData = upcomingBatchesData.map((item, index) => ({
    ...item,
    srNo:
      (pageNum - 1) * (dashboardData?.upcomingBatchesNoOfRecordsPerPage || 10) +
      index +
      1,
  }));

  const summaryCards = [
    {
      title: "Assigned Courses",
      value: summary.assignedCourses ?? 0,
      subtitle: "Courses assigned to the trainer",
      icon: "ni ni-books",
      theme: "purple",
      path: "/instructor/my-courses",
    },
    {
      title: "Assigned Batches",
      value: summary.assignedBatches ?? 0,
      subtitle: `${summary.upcomingBatches ?? 0} Upcoming · ${summary.ongoingBatches ?? 0} Ongoing · ${summary.completedBatches ?? 0} Completed`,
      icon: "ni ni-calendar-grid-58",
      theme: "green",
      path: "/instructor/batch",
    },
    {
      title: "Trainees",
      value: summary.totalTrainees ?? 0,
      subtitle: "Trainees enrolled in assigned batches",
      icon: "ni ni-single-02",
      theme: "blue",
      path: "/instructor/batch",
    },
    {
      title: "Assessments",
      value: summary.totalAssessments ?? 0,
      subtitle: "All assessments for assigned batches",
      icon: "ni ni-paper-diploma",
      theme: "indigo",
      path: "/instructor/assessment",
    },
  ];

  const upcomingBatchColumns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Batch Name",
      accessor: "batchName",
      minWidth: 180,
      maxWidth: 180,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Course Name",
      accessor: "courseName",
      minWidth: 220,
      maxWidth: 220,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Training Centre",
      accessor: "trainingCentre",
      minWidth: 170,
      maxWidth: 170,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Start Date",
      accessor: "startDate",
      minWidth: 120,
      maxWidth: 120,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "End Date",
      accessor: "endDate",
      minWidth: 120,
      maxWidth: 120,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
  ];

  return (
    <Container fluid className="admin-dashboard">
      <Row className="dashboard-summary-grid">
        {summaryCards.map((card) => renderSummaryCard(card, navigate))}
      </Row>

      <Row>
        <Col className="mb-4">
          <Card>
            {renderSectionHeader(
              "Upcoming Batches",
              null,
              "/instructor/batch",
              navigate,
            )}
            <CardBody>
              {listState.isLoading ? (
                <div className="loaderContainer">
                  <i className="fa fa-spinner fa-spin fa-2xl loader" />
                </div>
              ) : listState.error ? (
                <div className="loaderContainer error-style">
                  <div className="error-icon">
                    <i className="fa-solid fa-circle-exclamation"></i>
                  </div>
                  <span>{listState.error}</span>
                </div>
              ) : listState.noRecords || upcomingBatchesData.length === 0 ? (
                <div className="loaderContainer empty-style">
                  <div className="empty-icon">
                    <i className="fa-solid fa-circle-info"></i>
                  </div>
                  <span>No upcoming batches found.</span>
                </div>
              ) : (
                <div className="table-responsive">
                  <CommonTable
                    columns={upcomingBatchColumns}
                    data={tableData || []}
                    defaultPageSize={
                      dashboardData?.upcomingBatchesNoOfRecordsPerPage || 10
                    }
                    activePage={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      dashboardData?.upcomingBatchesTotalRecords /
                        dashboardData?.upcomingBatchesNoOfRecordsPerPage || 10,
                    )}
                    showPaginationBottom={true}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TrainerDashboard;
